import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Search, Filter, Save, Plus, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface AdvancedSearchCriteria {
  // Basic filters
  searchTerm?: string;
  categoryIds?: number[];
  tags?: string[];
  excludeTags?: string[];
  
  // Date filters
  createdAfter?: string;
  createdBefore?: string;
  updatedAfter?: string;
  updatedBefore?: string;
  
  // Metrics filters
  minUses?: number;
  maxUses?: number;
  minLikes?: number;
  maxLikes?: number;
  minComments?: number;
  maxComments?: number;
  minRating?: number;
  
  // Content filters
  minContentLength?: number;
  maxContentLength?: number;
  submittedBy?: string;
  
  // Search options
  scope?: 'all' | 'title' | 'content' | 'description' | 'tags';
  exactMatch?: boolean;
  categoryMode?: 'any' | 'all';
  tagMode?: 'any' | 'all';
  
  // Sorting
  sortBy?: string[];
  
  // Other options
  includeFacets?: boolean;
}

interface Category {
  id: number;
  name: string;
  color: string;
}

interface Tag {
  id: number;
  name: string;
}

interface AdvancedSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  criteria: AdvancedSearchCriteria;
  onCriteriaChange: (criteria: AdvancedSearchCriteria) => void;
  categories: Category[];
  availableTags: Tag[];
  onSearch: () => void;
  onReset: () => void;
  onSaveSearch?: (name: string, description: string) => void;
}

export const AdvancedSearchDialog: React.FC<AdvancedSearchDialogProps> = ({
  open,
  onOpenChange,
  criteria,
  onCriteriaChange,
  categories,
  availableTags,
  onSearch,
  onReset,
  onSaveSearch
}) => {
  const [saveSearchName, setSaveSearchName] = useState('');
  const [saveSearchDescription, setSaveSearchDescription] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const validate = (): boolean => {
    const errors: string[] = [];
    if (criteria.createdAfter && criteria.createdBefore && criteria.createdAfter > criteria.createdBefore) {
      errors.push('"Created Before" must be on or after "Created After".');
    }
    if (criteria.minUses !== undefined && criteria.maxUses !== undefined && criteria.minUses > criteria.maxUses) {
      errors.push('"Min Uses" must be less than or equal to "Max Uses".');
    }
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const updateCriteria = (updates: Partial<AdvancedSearchCriteria>) => {
    onCriteriaChange({ ...criteria, ...updates });
    if (validationErrors.length > 0) setValidationErrors([]);
  };

  const toggleTag = (tagName: string, isExclude: boolean = false) => {
    const field = isExclude ? 'excludeTags' : 'tags';
    const currentTags = criteria[field] || [];
    const isSelected = currentTags.includes(tagName);
    
    updateCriteria({
      [field]: isSelected 
        ? currentTags.filter(t => t !== tagName)
        : [...currentTags, tagName]
    });
  };

  const toggleCategory = (categoryId: number) => {
    const currentCategories = criteria.categoryIds || [];
    const isSelected = currentCategories.includes(categoryId);
    
    updateCriteria({
      categoryIds: isSelected
        ? currentCategories.filter(c => c !== categoryId)
        : [...currentCategories, categoryId]
    });
  };

  const handleSaveSearch = () => {
    if (onSaveSearch && saveSearchName.trim()) {
      onSaveSearch(saveSearchName.trim(), saveSearchDescription.trim());
      setSaveSearchName('');
      setSaveSearchDescription('');
      setShowSaveForm(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Advanced Search & Filters
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Search */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Basic Search</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="searchTerm">Search Term</Label>
                <Input
                  id="searchTerm"
                  placeholder="Enter keywords..."
                  value={criteria.searchTerm || ''}
                  onChange={(e) => updateCriteria({ searchTerm: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="scope">Search In</Label>
                <Select
                  value={criteria.scope || 'all'}
                  onValueChange={(value: string) =>
                    updateCriteria({ scope: value as AdvancedSearchCriteria['scope'] })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Fields</SelectItem>
                    <SelectItem value="title">Title Only</SelectItem>
                    <SelectItem value="content">Content Only</SelectItem>
                    <SelectItem value="description">Description Only</SelectItem>
                    <SelectItem value="tags">Tags Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="exactMatch"
                checked={criteria.exactMatch || false}
                onCheckedChange={(checked: boolean) => 
                  updateCriteria({ exactMatch: checked })
                }
              />
              <Label htmlFor="exactMatch">Exact match only</Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="submittedBy">Submitted By</Label>
              <Input
                id="submittedBy"
                placeholder="Username or name..."
                value={criteria.submittedBy || ''}
                onChange={(e) => updateCriteria({ submittedBy: e.target.value })}
              />
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Categories</h3>
              <Select
                value={criteria.categoryMode || 'any'}
                onValueChange={(value: string) =>
                  updateCriteria({ categoryMode: value as AdvancedSearchCriteria['categoryMode'] })
                }
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => {
                const isSelected = criteria.categoryIds?.includes(category.id) || false;
                return (
                  <Button
                    key={category.id}
                    type="button"
                    variant={isSelected ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleCategory(category.id)}
                    className={cn(
                      "transition-colors",
                      isSelected && "shadow-sm"
                    )}
                  >
                    {category.name}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Tags</h3>
              <Select
                value={criteria.tagMode || 'any'}
                onValueChange={(value: string) =>
                  updateCriteria({ tagMode: value as AdvancedSearchCriteria['tagMode'] })
                }
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-sm font-medium text-green-700">Include Tags</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {availableTags.slice(0, 20).map(tag => {
                    const isSelected = criteria.tags?.includes(tag.name) || false;
                    return (
                      <Button
                        key={`include-${tag.id}`}
                        type="button"
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleTag(tag.name, false)}
                        className={cn(
                          "h-8 text-xs transition-colors",
                          isSelected && "bg-green-600 hover:bg-green-700 border-green-600"
                        )}
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        {tag.name}
                      </Button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-red-700">Exclude Tags</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {availableTags.slice(0, 20).map(tag => {
                    const isSelected = criteria.excludeTags?.includes(tag.name) || false;
                    return (
                      <Button
                        key={`exclude-${tag.id}`}
                        type="button"
                        variant={isSelected ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleTag(tag.name, true)}
                        className={cn(
                          "h-8 text-xs transition-colors",
                          isSelected && "bg-red-600 hover:bg-red-700 border-red-600"
                        )}
                      >
                        <X className="w-3 h-3 mr-1" />
                        {tag.name}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Filters */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Metrics Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Uses</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={criteria.minUses || ''}
                    onChange={(e) => updateCriteria({ 
                      minUses: e.target.value ? parseInt(e.target.value) : undefined 
                    })}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={criteria.maxUses || ''}
                    onChange={(e) => updateCriteria({ 
                      maxUses: e.target.value ? parseInt(e.target.value) : undefined 
                    })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Likes</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={criteria.minLikes || ''}
                    onChange={(e) => updateCriteria({ 
                      minLikes: e.target.value ? parseInt(e.target.value) : undefined 
                    })}
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={criteria.maxLikes || ''}
                    onChange={(e) => updateCriteria({ 
                      maxLikes: e.target.value ? parseInt(e.target.value) : undefined 
                    })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Min Rating</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  placeholder="0.0 - 5.0"
                  value={criteria.minRating || ''}
                  onChange={(e) => updateCriteria({ 
                    minRating: e.target.value ? parseFloat(e.target.value) : undefined 
                  })}
                />
              </div>
            </div>
          </div>

          {/* Date Filters */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Date Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Created After</Label>
                <Input
                  type="date"
                  value={criteria.createdAfter || ''}
                  onChange={(e) => updateCriteria({ createdAfter: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Created Before</Label>
                <Input
                  type="date"
                  value={criteria.createdBefore || ''}
                  onChange={(e) => updateCriteria({ createdBefore: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Content Length */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Content Length</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Characters</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={criteria.minContentLength || ''}
                  onChange={(e) => updateCriteria({ 
                    minContentLength: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                />
              </div>
              <div className="space-y-2">
                <Label>Max Characters</Label>
                <Input
                  type="number"
                  placeholder="Unlimited"
                  value={criteria.maxContentLength || ''}
                  onChange={(e) => updateCriteria({ 
                    maxContentLength: e.target.value ? parseInt(e.target.value) : undefined 
                  })}
                />
              </div>
            </div>
          </div>

          {/* Advanced Options */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Advanced Options</h3>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeFacets"
                checked={criteria.includeFacets || false}
                onCheckedChange={(checked: boolean) => 
                  updateCriteria({ includeFacets: checked })
                }
              />
              <Label htmlFor="includeFacets">Include search facets (slower but more detailed results)</Label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pt-4 border-t">
          {validationErrors.length > 0 && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {validationErrors.map((err, i) => <div key={i}>{err}</div>)}
            </div>
          )}
          <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => { onReset(); setValidationErrors([]); }}>
              Reset All
            </Button>
            {onSaveSearch && (
              <>
                {!showSaveForm ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowSaveForm(true)}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Search
                  </Button>
                ) : (
                  <div className="flex gap-2 items-center">
                    <Input
                      placeholder="Search name"
                      value={saveSearchName}
                      onChange={(e) => setSaveSearchName(e.target.value)}
                      className="w-32"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSaveSearch}
                      disabled={!saveSearchName.trim()}
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setShowSaveForm(false);
                        setSaveSearchName('');
                        setSaveSearchDescription('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => { onOpenChange(false); setValidationErrors([]); }}>
              Cancel
            </Button>
            <Button type="button" onClick={() => { if (validate()) { onSearch(); onOpenChange(false); } }}>
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};